import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import customerService from '../services/customerService';
import transactionService from '../services/transactionService';
import { Customer } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  spopFilter?: string;
}

const CreateTransactionDialog: React.FC<Props> = ({ open, onClose, onCreated, spopFilter }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [type, setType] = useState<'topup' | 'payment' | 'deduction' | 'refund'>('topup');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [status, setStatus] = useState<'completed' | 'pending'>('completed');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoadingCustomers(true);
      try {
        const filters = spopFilter ? { spop_id: spopFilter } : undefined;
        const res = await customerService.getCustomers(1, 50, customerSearch || undefined, filters);
        setCustomers(res.data?.customers || []);
      } catch {
        setCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [open, customerSearch, spopFilter]);

  const resetForm = () => {
    setSelectedCustomer(null);
    setType('topup');
    setAmount('');
    setDescription('');
    setPaymentMethod('');
    setStatus('completed');
    setError(null);
    setCustomerSearch('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) {
      setError('Pilih pelanggan terlebih dahulu');
      return;
    }
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Jumlah harus lebih dari 0');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await transactionService.createTransaction({
        customer_id: selectedCustomer.id,
        type,
        amount: parsedAmount,
        description: description || undefined,
        payment_method: paymentMethod || undefined,
        status,
      } as any);
      onCreated();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal membuat transaksi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tambah Transaksi Manual</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Autocomplete
          options={customers}
          loading={loadingCustomers}
          value={selectedCustomer}
          onChange={(_, value) => setSelectedCustomer(value)}
          onInputChange={(_, value) => setCustomerSearch(value)}
          getOptionLabel={(option) =>
            `${option.name}${option.customer_code ? ` (${option.customer_code})` : ''}`
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Pelanggan" margin="normal" required size="small" />
          )}
        />

        <FormControl fullWidth margin="normal" size="small">
          <InputLabel>Jenis Transaksi</InputLabel>
          <Select value={type} label="Jenis Transaksi" onChange={(e) => setType(e.target.value as typeof type)}>
            <MenuItem value="topup">Top Up</MenuItem>
            <MenuItem value="payment">Pembayaran</MenuItem>
            <MenuItem value="deduction">Pemotongan</MenuItem>
            <MenuItem value="refund">Refund</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          size="small"
          label="Jumlah (Rp)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
          inputProps={{ min: 0, step: 1000 }}
        />

        <TextField
          fullWidth
          margin="normal"
          size="small"
          label="Deskripsi"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
        />

        <TextField
          fullWidth
          margin="normal"
          size="small"
          label="Metode Pembayaran"
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          placeholder="Transfer, tunai, Tripay, dll."
        />

        <FormControl fullWidth margin="normal" size="small">
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value as typeof status)}>
            <MenuItem value="completed">Selesai (langsung diproses)</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Batal
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={22} /> : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTransactionDialog;
